/* eslint-disable @typescript-eslint/no-explicit-any */
import { BigNumberish } from '@ethersproject/bignumber';
import { Contract } from '@ethersproject/contracts';
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers';
import { parseEther } from '@ethersproject/units';
import ERC20 from '../abi/ERC20.json';
import lixiBagAbi from '../abi/LixiBag.json';
import { fromWei, MAX_UINT128, toWei } from './contractUtils';

export const createCampaignOnSC = async (
  numMint: number,
  extraToken: string,
  extraTokenAmount: BigNumberish,
  receivers: string[],
  signer: JsonRpcSigner,
  value: BigNumberish,
  lixiBagAddress: string,
): Promise<any> => {
  const lixiBagContract = new Contract(lixiBagAddress, lixiBagAbi, signer);
  const amount = parseEther(value.toString());

  // Check if the campaign is still within the allowed time window
  try {
    const closingTime = await lixiBagContract.closingTime();
    const currentTime = Math.floor(Date.now() / 1000); // Current timestamp in seconds
    const closingTimeNumber = closingTime.toNumber();

    console.log('Campaign time check:', {
      currentTime,
      closingTime: closingTimeNumber,
      timeRemaining: closingTimeNumber - currentTime,
      isExpired: currentTime > closingTimeNumber,
    });

    if (currentTime > closingTimeNumber) {
      const timePassed = currentTime - closingTimeNumber;
      const hoursPassed = Math.floor(timePassed / 3600);
      const minutesPassed = Math.floor((timePassed % 3600) / 60);

      throw new Error(
        `Campaign has expired. The closing time passed ${hoursPassed}h ${minutesPassed}m ago. ` +
          `Please contact admin to extend the campaign period or create a new campaign.`,
      );
    }
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('Campaign has expired')
    ) {
      throw error;
    }
    // If we can't read the closing time, continue with the transaction
    // The contract will handle the validation
  }

  return lixiBagContract.mintLixi(
    Number(numMint),
    extraToken,
    extraTokenAmount,
    receivers,
    {
      value: amount,
    },
  );
};

export const listenEventOnCreateCampaign = (contract: any, callback: any) => {
  contract.listeners('MintLixi', event => {
    console.log('listeners');
    console.log(event);
    callback();
  });

  contract.on('MintLixi', event => {
    console.log('evenEEEEEEEt');
    console.log(event);
    callback();
  });
};

export const checkingAllowanceOnSC = async (
  owner: string,
  tokenAddress: string,
  library: Web3Provider,
  lixiBagAddress: string,
): Promise<boolean> => {
  const ERC20Contract = new Contract(tokenAddress, ERC20, library);
  const res = await ERC20Contract.allowance(owner, lixiBagAddress);
  return fromWei(res) !== '0';
};

export const approveOnSC = async (
  tokenAddress: string,
  signer: JsonRpcSigner,
  lixiBagAddress: string,
): Promise<boolean> => {
  const ERC20Contract = new Contract(tokenAddress, ERC20, signer);
  await ERC20Contract.approve(lixiBagAddress, toWei(MAX_UINT128));
  return true;
};

export const erc20DecimalsOnSc = async (
  tokenAddress: string,
  library: Web3Provider,
): Promise<number> => {
  const ERC20Contract = new Contract(tokenAddress, ERC20, library);
  return ERC20Contract.decimals();
};

export const addWhitelister = async (
  groupId: number | undefined,
  userAddress: string | undefined,
  signer: JsonRpcSigner | undefined,
  isAdmin = false,
  lixiBagAddress: string,
) => {
  const lixiBagContract = new Contract(lixiBagAddress, lixiBagAbi, signer);
  if (isAdmin) {
    await lixiBagContract.addWhitelisterByAdmin(groupId, userAddress);
  } else {
    await lixiBagContract.addWhitelister(groupId, userAddress);
  }
};

export const addManyWhitelister = async (
  groupId: number | undefined,
  userAddresses: (string | undefined)[],
  signer: JsonRpcSigner | undefined,
  isAdmin = false,
  lixiBagAddress: string,
) => {
  const lixiBagContract = new Contract(lixiBagAddress, lixiBagAbi, signer);
  if (isAdmin) {
    await lixiBagContract.addManyWhitelistersByAdmin(groupId, userAddresses);
  } else {
    await lixiBagContract.addManyWhitelisters(groupId, userAddresses);
  }
};

export const claimLixiByOwnerOrAdmin = async (
  groupId: number | undefined,
  userAddress: string | undefined,
  signer: JsonRpcSigner | undefined,
  isAdmin = false,
  lixiBagAddress: string,
) => {
  const lixiBagContract = new Contract(lixiBagAddress, lixiBagAbi, signer);
  if (isAdmin) {
    await lixiBagContract.claimLixiByAdmin(groupId, userAddress);
  } else {
    await lixiBagContract.claimLixiByOwner(groupId, userAddress);
  }
};

export const claimLixi = async (
  groupId: number | undefined,
  signer: JsonRpcSigner | undefined,
  lixiBagAddress: string,
) => {
  const lixiBagContract = new Contract(lixiBagAddress, lixiBagAbi, signer);
  return await lixiBagContract.claimLixi(groupId);
};

export const removeWhitelister = async (
  groupId: number | undefined,
  userAddress: string | undefined,
  signer: JsonRpcSigner | undefined,
  lixiBagAddress: string,
) => {
  const lixiBagContract = new Contract(lixiBagAddress, lixiBagAbi, signer);
  await lixiBagContract.removeWhitelister(groupId, userAddress);
};

export const claimGlobalJackpot = async (
  signer: JsonRpcSigner | undefined,
  lixiBagAddress: string,
) => {
  const lixiBagContract = new Contract(lixiBagAddress, lixiBagAbi, signer);
  return await lixiBagContract.claimGlobalJackpot();
};

export const checkCampaignTimeStatus = async (
  lixiBagAddress: string,
  library: Web3Provider,
) => {
  try {
    const lixiBagContract = new Contract(lixiBagAddress, lixiBagAbi, library);
    const closingTime = await lixiBagContract.closingTime();
    const currentTime = Math.floor(Date.now() / 1000);
    const closingTimeNumber = closingTime.toNumber();

    const timeRemaining = closingTimeNumber - currentTime;
    const isExpired = currentTime > closingTimeNumber;

    return {
      currentTime,
      closingTime: closingTimeNumber,
      timeRemaining,
      isExpired,
      closingTimeDate: new Date(closingTimeNumber * 1000),
      currentTimeDate: new Date(currentTime * 1000),
    };
  } catch (error) {
    console.error('Error checking campaign time status:', error);
    throw error;
  }
};
